import { useEffect, useMemo, useCallback } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useServices } from '@/services-context';
import styles from './search-page.module.scss';
import { FixLoader } from "@/elements-react/fix-loader";
import FixBackButton from '@/elements-react/fix-back-button';
import { LocationHook, RouteHook, useLocation, useRoute } from 'preact-iso';
import { pageSuspense, useSuspenseLoader } from '@/helpers/suspense';
import { ProductAreaContext } from '@/elements-react/product-area/context';
import FixBreadcrumb, { useBreadcrumbs } from '@/elements-react/fix-breadcrumb';
import { Url } from '@/helpers/url';
import ProductAreaElement from '@/elements-react/product-area-element';
import { useLayoutType, LayoutStyleTypes, LayoutTypes } from "@/util-react/layout";
import { useProductAreaStore } from '@/elements-react/product-area/store/hook';
import { createLocationParams, getLocationValues } from '@/elements-react/product-area/store/location-utils';
import { FiltersChangeHandler } from '@/elements-react/product-area/store/store';
import { AreaFilters, getHash } from '@/elements-react/product-area/store/area-filters';
import { FORMATTER_LISTENER_NAME } from "@/services/formatter.ts";
import { CatalogLayout } from "@/elements-react/catalog-layout";
import { B2cProductSearchPayload } from '@/generated/graphql/b2c';
import { CatalogPageStore } from '../catalog-page-common/page-store';
import { useStoreSync } from '@/elements-react/product-area/store/sync';
import { useRef } from "preact/hooks";

export const SearchPage = pageSuspense(() => {
    const route = useRoute();
    const location = useLocation();
    const services = useServices([FORMATTER_LISTENER_NAME]);
    const filters = getLocationValues(location);
    const searchQuery = filters.search;
    const sendGoogleEventViewItemList = useRef<() => void | null>(null);

    const store = useSuspenseLoader({
        key: "search-route",
        loader: async () => {
            await services.initialize.prepare({
                location: location,
                route: route,
            });

            const loader = new CatalogPageStore(services);
            loader.setContext({ search: searchQuery! });

            await loader.loadForLocation({ location: location, route: route });

            return loader;
        }
    });

    useStoreSync(store.sync);

    const updatePageData = useCallback(async (params: { route: RouteHook, location: LocationHook, filters: AreaFilters }) => {
        const searchQuery = params.filters.search;

        store.setContext({ search: searchQuery! });
        await store.loadForLocation({
            location: params.location,
            route: params.route,
        });
    }, [store]);

    const state = store.result();

    useEffect(() => {
        return () => {
            store.release();
        };
    }, [route.path]);

    useEffect(() => {
        updatePageData({
            route: route,
            location: location,
            filters: filters,
        });
    }, [route.path, location, getHash(filters)]);

    useEffect(() => {
      if (sendGoogleEventViewItemList.current && state.loadedProducts.length > 0) {
        sendGoogleEventViewItemList.current();
      }
    }, [JSON.stringify(state.loadedProducts)]);

    const products: B2cProductSearchPayload[] = useMemo(() => {
        return Object.values(state.groups).reduce((acc, group) => {
            return acc.concat(group.products);
        }, [] as B2cProductSearchPayload[]);
    }, [Object.keys(state.groups).join("-")]);

    const brands = useMemo(() => [], []);

    const categories = useMemo(() => [], []);

    const { t } = useTranslation();

    useEffect(() => {
        updatePageData({
            route: route,
            location: location,
            filters: filters,
        });
    }, [route, location, getHash(filters)]);

    const breadcrumbs = useMemo(() => {
        const { search } = createLocationParams(filters);

        return [
            {
                label: t("0x619y4k02smw9cs.search", "Search"),
                url: Url.to("search-page", {}, search.toString()),
            }
        ];
    }, [filters.search]);

    const { layoutType, defaultLayoutStyleType } = useLayoutType();
    const type = defaultLayoutStyleType as LayoutStyleTypes.TYPE_LG | LayoutStyleTypes.TYPE_SM;
    const isDesktop = layoutType === LayoutTypes.TYPE_DESKTOP;

    const navigate = useCallback((params: { params?: URLSearchParams, mode?: 'pushState' | 'replaceState' }) => {
        const searchParams = params.params ?? new URLSearchParams();
        const url = Url.to("search-page", {}, searchParams.toString());
        const mode = params.mode ?? 'replaceState';
        location.route(url, mode === 'replaceState');
    }, []);

    const onFiltersChange: FiltersChangeHandler = useCallback((filters) => {
        const { search } = createLocationParams(filters);
        navigate({
            params: search,
        });
    }, []);

    const areaStore = useProductAreaStore({
        filters,
        brands,
        categories,
        products,
        more: state.more,
        onFiltersChange: onFiltersChange,
        totalItems: state.totalItems,
    });

    const renderTopcard = () => (
        <div className={classNames(styles.section, styles.normargin, styles.card, styles['type-card-top'])}>
            <div className={classNames(styles.container)}>
                {/* TODO: Display alerts when needed (already exists in main layout?)
                    <div className={classNames(styles.row, styles.alerts)}>
                        <FixAlert />
                    </div>
                */}

                <div className={classNames(styles.row, styles.navigation)}>
                    <div>
                        {/* TODO: Display back button before breadcrumb and fix it ckick event (back in history?) */}
                        <FixBackButton className="back" mode="link" />
                    </div>

                    <FixBreadcrumb />
                </div>

                <div className={classNames(styles.row, styles.primary)}>
                    <h1 className={classNames(styles['section-title'], styles.multipleSectionTitle)}>
                        <span className={classNames(styles['title-text'])}>
                            {t("j8cinnz091w154tp.search-results", "Search results")}
                        </span>

                        <span className={classNames(styles['title-for'])}>
                            {t("43ook0siipp3ufhv.for-name", "for {{name}}", { name: searchQuery })}
                        </span>
                    </h1>

                    {isDesktop && (
                        <>
                            <div className={classNames(styles.results)}>{t("j8cinnz091w154tp.results", "results")}</div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );

    useBreadcrumbs(breadcrumbs);

    return (
        <CatalogLayout>
            <ProductAreaContext.Provider value={areaStore}>
                {state.loading && (<FixLoader />)}
                {!state.blank && (
                    <div className={classNames(styles[type])}>
                        {renderTopcard()}
                        <ProductAreaElement type={type} resultState={state} pageType="search" sendAction={sendGoogleEventViewItemList} />
                    </div>
                )}
            </ProductAreaContext.Provider>
        </CatalogLayout>
    );
});

export default SearchPage;